import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { Popover } from 'primeng/popover';
import { BehaviorSubject, firstValueFrom, Subscription } from 'rxjs';
import { GeneralParameterEnums } from 'src/app/enum/general-parameter-enums';
import { LoginService } from 'src/app/services/authentication/login.service';
import { UserService } from 'src/app/services/authentication/user.service';
import { UserAuthenticatedService } from 'src/app/services/global-services/user-authenticated.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { BranchService } from 'src/app/services/management/branch.service';
import { CompanyService } from 'src/app/services/management/company.service';
import { GeneralParameterService } from 'src/app/services/management/general-parameter.service';
import { ArticleTaskService } from 'src/app/services/others/article-task.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { UpdatePasswordComponent } from 'src/app/views/authentication/users/update-password/update-password.component';
import { PendingArticleTaskComponent } from 'src/app/views/others/article-task/pending-article-task/pending-article-task.component';
import Swal from 'sweetalert2';
import { CustomModalComponent } from '../custom-modal/custom-modal.component';
@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  imports: [
    Popover,
    InputGroupAddonModule,
    ButtonModule,
    InputTextModule,
    CommonModule,
    CustomModalComponent
  ],
  standalone: true,
})
export class HeaderComponent implements OnInit {
  @ViewChild('updatePasswordUser') updatePasswordUserModalComponent?: CustomModalComponent | null = null;
  @ViewChild('articleTaskModal') articleTaskModalComponent?: CustomModalComponent | null = null;
  @Output() expandMenu = new EventEmitter<boolean>(false);

  companyId: number = 0;
  companyName: string = '';
  componentArticleTask = PendingArticleTaskComponent;
  icon: string = 'pi pi-home';
  inputsForArticleTask$ = new BehaviorSubject<any>({
    listArticleTasks: [],
  });
  listObservers: Array<Subscription> = new Array<Subscription>();
  userName: string = '';
  userInitials: string = '';
  swArticleTask: boolean = false;
  title: string = 'Cervino';
  totalTasks: number = 0;
  updatePasswordComponent = UpdatePasswordComponent;
  readonly EMAIL = 'as.altos.software@gmail.com';

  constructor(
    private articleTaskService: ArticleTaskService,
    private branchService: BranchService,
    private generalParameterService: GeneralParameterService,
    private loginService: LoginService,
    private router: Router,
    private companyService: CompanyService,
    private userService: UserService,
    private userAuthenticatedService: UserAuthenticatedService,
    private headerService: HeaderService,
    private toastHandlerService: ToastHandlerService,
  ) { }

  async ngOnInit(): Promise<void> {
    try {
      await Promise.all([
        await this.getUserAutheticated(),
        this.getCompanyAutheticated(),
        this.getFirstBranchByUserAuthenticatedAsync(),
        this.getGeneralParametersByCompany(),
      ]);

      this.handleArticleTask();
    } catch (error) {
      console.error(
        'Error al cargar los datos de la compañía y usuario:',
        error
      );
    }

    this.headerService.title$.subscribe((newTitle) => {
      this.title = newTitle;
    });

    this.headerService.icon$.subscribe((newIcon) => {
      this.icon = newIcon;
    });
  }

  ngOnDestroy() {
    this.listObservers.forEach((sub) => sub.unsubscribe());
  }

  emitClick() {
    this.expandMenu.emit(true);
  }

  async getUserAutheticated() {
    try {
      const response = await firstValueFrom(
        this.userService.getUserAutheticated()
      );
      this.userAuthenticatedService.assignUser(response);
      this.companyId = response.companyId;
      this.companyName = response.company!.name;
      this.userName = `${response.firstName} ${response.lastName}`;
      this.userInitials = `${response.firstName?.[0] ?? ''}${response.lastName?.[0] ?? ''
        }`;
      return true;
    } catch (error) {
      console.error('Error al obtener el usuario autenticado:', error);
      throw new Error('Error al obtener el usuario autenticado');
    }
  }

  async getCompanyAutheticated() {
    try {
      const response = await firstValueFrom(
        this.companyService.getCompanyById(this.companyId)
      );
      this.userAuthenticatedService.assignCompany(response);
      return true;
    } catch (error) {
      console.error('Error al obtener la compañía autenticada:', error);
      throw new Error('Error al obtener la compañía autenticada');
    }
  }

  async getFirstBranchByUserAuthenticatedAsync() {
    try {
      const response = await firstValueFrom(
        this.branchService.getFirstBranchByUserAuthenticatedAsync()
      );
      this.userAuthenticatedService.assignFirstBranchByUser(response);
      return true;
    } catch (error) {
      console.error('Error al obtener la primera sucursal:', error);
      throw new Error('Error al obtener la primera sucursal');
    }
  }

  async getGeneralParametersByCompany() {
    try {
      const response = await firstValueFrom(
        this.generalParameterService.getGeneralParametersByCompany(
          this.companyId
        )
      );
      this.userAuthenticatedService.assignListGeneralParametersByCompany(
        response
      );
      this.swArticleTask = response.some(x => x.generalParameterId === GeneralParameterEnums.tareaArticulos);
      return true;
    } catch (error) {
      console.error(
        'Error al obtener los parámetros generales de la empresa:',
        error
      );
      throw new Error(
        'Error al obtener los parámetros generales de la empresa'
      );
    }
  }

  async handleArticleTask() {
    if (this.swArticleTask) {
      const url = this.router.url as string;
      if (url === '/main') {
        await this.getArticleTaskPending(false);
      } else {
        await this.getTotalArticleTaskPendingAsync();
      }
    }
  }

  async getArticleTaskPending(showAlert: boolean) {
    try {
      const articleTaskPending = await firstValueFrom(this.articleTaskService.getArticleTaskPending());
      this.totalTasks = articleTaskPending.length;

      if (this.totalTasks > 0) {
        this.inputsForArticleTask$.next({
          listArticleTasks: articleTaskPending,
        });
        this.articleTaskModalComponent?.show();
      }

      if (this.totalTasks == 0 && showAlert) {
        this.toastHandlerService.showToast('info', 'No hay tareas pendientes', '');
      }
    } catch (error) {
      console.error('Error al buscar las tareas pendientes.', error);
    }
  }

  async getTotalArticleTaskPendingAsync() {
    try {
      const response = await firstValueFrom(this.articleTaskService.getTotalArticleTaskPendingAsync());
      this.totalTasks = response;
    } catch (error) {
      console.error('Error al obtener el total de tareas pendientes:', error
      );
      throw new Error('Error al obtener el total de tareas pendientes');
    }
  }

  async copyEmailToClipboard() {
    try {
      await navigator.clipboard.writeText(this.EMAIL);

      this.toastHandlerService.showToast('info', 'El correo de soporte se copió al portapapeles', '');
    } catch (err) {
      console.error('Error al copiar el correo de soporte:', err);
      this.toastHandlerService.showToast('error', 'Error al copiar el correo de soporte', '¡Atención!');
    }
  }

  logoutUser() {
    Swal.fire({
      title: '¿Está seguro que desea cerrar sesión?',
      text: 'Será redirigido a la página de inicio de sesión.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, cerrar sesión',
      cancelButtonText: 'Cancelar',
    }).then((result) => {
      if (result.isConfirmed) {
        this.loginService.logoutUser().subscribe(() => { });
        this.router.navigate([`/login`]);
      }
    });
  }

  openPasswordModal() {
    this.updatePasswordUserModalComponent && this.updatePasswordUserModalComponent.show();
  }
}
